// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../../../services/api';
import CumulativeFlowDiagram, { CumulativeFlowDiagramRef } from './CumulativeFlowDiagram';
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SelectProjectRelease from '../../SelectProjectRelease';
import { IProjectModel } from '../../../models/ProjectModel';

interface DiagramContainerProps {
  project: IProjectModel;
  defaultReleaseId: number;
  open: boolean;
  onClose: () => void;
  routes: any;
}

const DiagramContainer: React.FC<DiagramContainerProps> = ({
  project,
  defaultReleaseId,
  open,
  onClose,
  routes,
}) => {
  const diagramRef = useRef<CumulativeFlowDiagramRef>(null);
  const [data, setData] = useState<any>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedReleaseId, setSelectedReleaseId] = useState<number | null>(
    defaultReleaseId > 0 ? defaultReleaseId : null
  );

  const fetchData = async (startDate?: string, endDate?: string) => {
    try {
      const startDateTime = startDate ? new Date(startDate).toISOString() : undefined;
      const endDateTime = endDate ? new Date(endDate).toISOString() : undefined;
      const params = {
        projectId: project.id,
        ...(selectedReleaseId && { releaseId: selectedReleaseId }),
        ...(startDateTime && { startDate: startDateTime }),
        ...(endDateTime && { endDate: endDateTime }),
      };
      const response = await api.get(`/umbraco/api/Project/GetCumulativeFlowDiagram`, { params });

      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (project.id) {
      fetchData();
    }
  }, [project.id, selectedReleaseId]);

  const handleFilterClick = () => {
    if (!startDate || !endDate) return;
    fetchData(startDate, endDate);
  };
  const handleHighlight = () => {
    if (diagramRef.current) {
      diagramRef.current.highlightSuperPhases();
    }
  };
  const handleClearDateFilters = () => {
    const hadFilters = !!startDate || !!endDate;

    setStartDate('');
    setEndDate('');
    if (hadFilters) {
      fetchData();
    }
  };

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          boxShadow: 'none',
          borderRadius: 0,
          width: '100%',
          height: '100%',
          position: 'initial',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{project.projectName}</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box display="flex" alignItems="flex-end" gap={2} mt={2}>
          <Box sx={{ maxWidth: 500 }}>
            <Typography variant="body2" color="textSecondary">
              Release
            </Typography>

            <SelectProjectRelease
              onChange={(id: number) => setSelectedReleaseId(id > 0 ? id : null)}
              releases={project.projectReleases}
              defaultReleaseId={selectedReleaseId ?? 0}
              routes={routes.editProjectReleaseDetailRoute}
              renderContent={false}
              disablePortal={true}
              size="medium"
            />
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <DatePicker
              label="Start Date"
              format="DD/MM/YYYY"
              value={startDate && dayjs(startDate).isValid() ? dayjs(startDate) : null}
              onChange={(newValue) => {
                if (newValue) {
                  const formatted = dayjs(newValue).format('YYYY-MM-DD');
                  setStartDate(formatted);
                } else {
                  setStartDate('');
                }
              }}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: { maxWidth: 300 },
                },
              }}
            />
            <DatePicker
              label="End Date"
              format="DD/MM/YYYY"
              value={endDate && dayjs(endDate).isValid() ? dayjs(endDate) : null}
              onChange={(newValue) => {
                if (newValue) {
                  const formatted = dayjs(newValue).format('YYYY-MM-DD');
                  setEndDate(formatted);
                } else {
                  setEndDate('');
                }
              }}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: { maxWidth: 300 },
                },
              }}
            />
          </LocalizationProvider>
          <Button variant="contained" onClick={handleFilterClick} sx={{ height: '56px' }}>
            Filter
          </Button>
          <Button variant="contained" onClick={handleClearDateFilters} sx={{ height: '56px' }}>
            Clear Date Filters
          </Button>
          <Button variant="contained" onClick={handleHighlight} sx={{ height: '56px' }}>
            Clear Phase Filters
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {data && (
          <CumulativeFlowDiagram
            ref={diagramRef}
            title={data.title}
            xLabel={data.xLabel}
            yLabel={data.yLabel}
            labels={data.labels}
            dataPoints={data.dataPoints}
            colorPalette={data.colorPalette}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DiagramContainer;
